import { useState } from 'react';
import { Plus, Check, Lock, ChevronRight, LogOut } from 'lucide-react';
import BottomSheet from '../BottomSheet';

interface Account {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  isActive: boolean;
}

interface SwitchAccountsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SwitchAccountsBottomSheet({ isOpen, onClose }: SwitchAccountsBottomSheetProps) {
  // Mock accounts data - in a real app, this would come from an API or auth context
  const [accounts, setAccounts] = useState<Account[]>([
    {
      id: '1',
      username: 'johndoe',
      displayName: 'John Doe',
      avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1760&q=80',
      isActive: true
    },
    {
      id: '2',
      username: 'janedoe',
      displayName: 'Jane Doe',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
      isActive: false
    },
    {
      id: '3',
      username: 'marksmith',
      displayName: 'Mark Smith',
      avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
      isActive: false
    }
  ]);

  // Handle switching to a different account
  const handleSwitchAccount = (accountId: string) => {
    // In a real app, this would authenticate as the selected account
    setAccounts(accounts.map(account => ({
      ...account,
      isActive: account.id === accountId
    })));
    
    // Close the dialog after switching
    setTimeout(() => {
      onClose();
    }, 500);
  };

  // Handle logging out of an account
  const handleLogout = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // In a real app, this would log out the specific account
    setAccounts(accounts.filter(account => account.id !== accountId));
  };

  return (
    <BottomSheet 
      isOpen={isOpen} 
      onClose={onClose}
      title="Switch Accounts"
      initialPosition="full"
      fullPageOnMobile={true}
      className="z-[200]"
    >
      <div className="max-h-[calc(100vh-100px)] overflow-y-auto">
        {/* Accounts List */}
        <div className="p-2">
          {accounts.map(account => (
            <div 
              key={account.id}
              onClick={() => handleSwitchAccount(account.id)}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-brand-green/20 dark:hover:bg-brand-green/30 cursor-pointer transition-colors"
            >
              <div className="flex items-center">
                <div className="relative">
                  <img 
                    src={account.avatar} 
                    alt={account.displayName} 
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  {account.isActive && (
                    <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-1 border-2 border-white dark:border-gray-900">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900 dark:text-white">{account.displayName}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">@{account.username}</p>
                </div>
              </div>
              <div className="flex items-center">
                {account.isActive ? (
                  <span className="text-xs font-medium text-green-500 mr-2">Active</span>
                ) : (
                  <button
                    onClick={(e) => handleLogout(account.id, e)}
                    className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 mr-2"
                    title="Log out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          ))}
        </div>

        {/* Add Account Button */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button 
            className="w-full flex items-center justify-center p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span>Add Account</span>
          </button>
        </div>

        {/* Privacy Note */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-start mb-2">
            <Lock className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
            <p>
              Your account information is stored securely on this device. 
              We never share your login details with third parties.
            </p>
          </div>
          <p className="ml-6">
            Learn more about <a href="#" className="text-blue-500 hover:underline">account switching</a> and <a href="#" className="text-blue-500 hover:underline">privacy</a>.
          </p>
        </div>
      </div>
    </BottomSheet>
  );
}
